import { useEffect, useMemo, useCallback } from "react";
import { useUnit } from "effector-react";
import { useNavigate } from "react-router-dom";
import { TreeNode } from "primereact/treenode";
import { TreeCheckboxSelectionKeys } from "primereact/tree";
import { ROUTES } from "@/core/constants/app";
import {
  $sectorsTerms,
  $expertiseTerms,
  $countriesTerms,
  $organizationCategoriesTerms,
  fetchTermsRequested,
} from "@/core/store/terms";
import { $userBasicInfo, fetchUserBasicInfoFx } from "@/features/user/store";
import {
  $accountSetupStore,
  setCurrentStep,
  updateFormData,
  setJobTitle,
  setSelectedNodes,
  toggleExpertise,
  setSelectedOrgSectors,
  setOrgDescription,
  setOrganizationSearchQuery,
  searchOrganizationsRequested,
  clearOrganizationSearchResults,
  selectOrganization,
  removeOrganization,
  fetchProposedPartnersRequested,
  fetchPlansRequested,
  completeAccountSetupFx,
  toggleSectorExpansion,
  setOrganizationDetails,
} from "../store";
import {
  OrganizationSearchResult,
  OrganizationDetails,
  ProposedPartner,
} from "../model/types";

export const useAccountSetupViewModel = (
  initialStep: number = 2,
  isModal?: boolean,
  onComplete?: () => void,
  submitPartnerOnly?: boolean,
) => {
  const navigate = useNavigate();

  // Store subscriptions
  const [
    accountSetupState,
    goToStep,
    setFormData,
    submitJobTitle,
    updateSelectedNodes,
    toggleExpertiseSelection,
    updateSelectedOrgSectors,
    updateOrgDescription,
    setSearchQuery,
    searchOrganizations,
    clearSearchResults,
    addOrganization,
    deleteOrganization,
    fetchProposedPartners,
    fetchPlans,
    toggleSector,
    submitOrganizationDetails,
  ] = useUnit([
    $accountSetupStore,
    setCurrentStep,
    updateFormData,
    setJobTitle,
    setSelectedNodes,
    toggleExpertise,
    setSelectedOrgSectors,
    setOrgDescription,
    setOrganizationSearchQuery,
    searchOrganizationsRequested,
    clearOrganizationSearchResults,
    selectOrganization,
    removeOrganization,
    fetchProposedPartnersRequested,
    fetchPlansRequested,
    toggleSectorExpansion,
    setOrganizationDetails,
  ]);

  // Terms stores
  const sectorsTerms = useUnit($sectorsTerms);
  const expertiseTerms = useUnit($expertiseTerms);
  const countriesTerms = useUnit($countriesTerms);
  const organizationCategoriesTerms = useUnit($organizationCategoriesTerms);
  const [userBasicInfo] = useUnit([$userBasicInfo]);

  const { partner } = userBasicInfo || {};
  const skipOrgSteps = typeof partner === "object" && partner !== null;

  // Initialize step on mount
  useEffect(() => {
    goToStep(initialStep);
    fetchTermsRequested({ vocId: "organization_categories" });
    fetchTermsRequested({ vocId: "countries" });

    // If starting at step 6, pre-set organization action to 'create'
    if (initialStep === 6) {
      setFormData({ organizationAction: "create" });
    }
  }, []);

  // Transform sectors to tree nodes
  const sectorTreeNodes = useMemo(() => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) return [];
    return transformToTreeNodes(sectorsTerms.data);
  }, [sectorsTerms.data]);

  // Get initial expanded keys
  const initialExpandedKeys = useMemo(() => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) return {};
    const keys: Record<string, boolean> = {};
    sectorsTerms.data
      .filter((item) => item.parent === "0")
      .forEach((item) => {
        keys[item.tid] = true;
      });
    return keys;
  }, [sectorsTerms.data]);

  // Country options for dropdown
  const countryOptions = useMemo(() => {
    return countriesTerms.data.map((term) => ({
      label: term.name,
      value: term.name,
      tid: term.tid,
    }));
  }, [countriesTerms.data]);

  // Expertise options
  const expertiseOptions = useMemo(() => {
    return (
      expertiseTerms.data?.map((term) => term.name) || [
        "Proposal Writing",
        "Project Management",
        "Financial Management",
        "Communication and Dissemination",
        "Partnership Development",
      ]
    );
  }, [expertiseTerms.data]);

  // Fetch proposed partners when reaching step 5
  useEffect(() => {
    if (accountSetupState.currentStep === 5) {
      fetchProposedPartners();
    }
  }, [accountSetupState.currentStep, fetchProposedPartners]);

  // Fetch plans when reaching step 9 with org creation
  useEffect(() => {
    if (
      accountSetupState.currentStep === 9 &&
      accountSetupState.formData.organizationAction === "create" &&
      accountSetupState.formData.organizationDetails &&
      accountSetupState.plans.length === 0 &&
      !accountSetupState.plansLoading
    ) {
      fetchPlans();
    }
  }, [
    accountSetupState.currentStep,
    accountSetupState.formData.organizationAction,
    accountSetupState.formData.organizationDetails,
    accountSetupState.plans.length,
    accountSetupState.plansLoading,
    fetchPlans,
  ]);

  // Debounced organization search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (accountSetupState.organizationSearchQuery.length >= 3) {
        searchOrganizations(accountSetupState.organizationSearchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [accountSetupState.organizationSearchQuery, searchOrganizations]);

  // Extract selected items from tree selection
  const getSelectedInterests = useCallback((): Array<{
    tid: string;
    name: string;
  }> => {
    if (!accountSetupState.selectedNodes) return [];
    const items: Array<{ tid: string; name: string }> = [];
    Object.keys(accountSetupState.selectedNodes).forEach((key) => {
      const node = findNodeByKey(sectorTreeNodes, key);
      if (node) {
        items.push({ tid: node.key as string, name: node.label as string });
      }
    });
    return items;
  }, [accountSetupState.selectedNodes, sectorTreeNodes]);

  // Extract selected sector labels
  const getSelectedOrgSectorLabels = useCallback((): string[] => {
    if (!accountSetupState.selectedOrgSectors) return [];
    const labels: string[] = [];
    Object.keys(accountSetupState.selectedOrgSectors).forEach((key) => {
      const node = findNodeByKey(sectorTreeNodes, key);
      if (node) {
        labels.push(node.label as string);
      }
    });
    return labels;
  }, [accountSetupState.selectedOrgSectors, sectorTreeNodes]);

  // Remove interest from selection
  const removeInterest = useCallback(
    (tid: string) => {
      if (!accountSetupState.selectedNodes) return;
      const newSelection = { ...accountSetupState.selectedNodes };
      delete newSelection[tid];
      updateSelectedNodes(
        Object.keys(newSelection).length > 0 ? newSelection : null,
      );
    },
    [accountSetupState.selectedNodes, updateSelectedNodes],
  );

  // Remove org sector from selection
  const removeOrgSector = useCallback(
    (sectorLabel: string) => {
      if (!accountSetupState.selectedOrgSectors) return;
      const key = findKeyByLabel(sectorTreeNodes, sectorLabel);
      if (key) {
        const newSelection = { ...accountSetupState.selectedOrgSectors };
        delete newSelection[key];
        updateSelectedOrgSectors(
          Object.keys(newSelection).length > 0 ? newSelection : null,
        );
      }
    },
    [
      accountSetupState.selectedOrgSectors,
      sectorTreeNodes,
      updateSelectedOrgSectors,
    ],
  );

  // Convert ProposedPartner to OrganizationSearchResult
  const convertToSearchResult = useCallback(
    (partner: ProposedPartner): OrganizationSearchResult => {
      return {
        id: partner.id,
        title: partner.title,
        created: "",
        field_created: "",
        logo_uri: partner.logo_uri || "",
        partner_type: { id: "", name: partner.org_type },
        since: partner.since,
        legal_form: "",
        country: { id: "", name: partner.location },
        city: "",
        pic_number: partner.pic || "",
        size: partner.size.length > 0 ? partner.size[0].name : "",
        implemented_projects: partner.programmes_count,
        sectors: partner.sectors.map((s) => ({ id: s.id, name: s.name })),
      };
    },
    [],
  );

  // Step navigation handlers
  const handleSubmitJobTitle = useCallback(
    (jobTitle: string) => {
      submitJobTitle(jobTitle);
      goToStep(3);
    },
    [submitJobTitle, goToStep],
  );

  const handleSubmitInterests = useCallback(() => {
    const selectedInterests = getSelectedInterests();
    setFormData({ interests: selectedInterests });
    goToStep(4);
  }, [getSelectedInterests, setFormData, goToStep]);

  const handleSubmitExpertise = useCallback(() => {
    setFormData({ euExpertise: accountSetupState.selectedExpertise });
    goToStep(skipOrgSteps ? 9 : 5);
  }, [
    accountSetupState.selectedExpertise,
    setFormData,
    goToStep,
    skipOrgSteps,
  ]);

  const handleSkipExpertise = useCallback(() => {
    setFormData({ euExpertise: [] });
    goToStep(skipOrgSteps ? 9 : 5);
  }, [setFormData, goToStep, skipOrgSteps]);

  const handleCreateOrganization = useCallback(() => {
    setFormData({ organizationAction: "create" });
    goToStep(6);
  }, [setFormData, goToStep]);

  const handleJoinOrganizations = useCallback(() => {
    if (accountSetupState.selectedOrganizations.length > 0) {
      const orgNames = accountSetupState.selectedOrganizations
        .map((org) => org.title)
        .join(", ");
      setFormData({
        organizationAction: "join",
        selectedOrganization: orgNames,
      });
      goToStep(9);
    }
  }, [accountSetupState.selectedOrganizations, setFormData, goToStep]);

  const handleSkipOrganization = useCallback(() => {
    setFormData({ organizationAction: "skip" });
    goToStep(9);
  }, [setFormData, goToStep]);

  const handleSubmitOrganizationDetails = useCallback(
    (details: OrganizationDetails) => {
      submitOrganizationDetails(details);
      goToStep(7);
    },
    [submitOrganizationDetails, goToStep],
  );

  const handleSubmitOrganizationSectors = useCallback(() => {
    const selectedSectors = getSelectedOrgSectorLabels();
    setFormData({ organizationSectors: selectedSectors });
    goToStep(8);
  }, [getSelectedOrgSectorLabels, setFormData, goToStep]);

  const handleSkipOrganizationSectors = useCallback(() => {
    setFormData({ organizationSectors: [] });
    goToStep(8);
  }, [setFormData, goToStep]);

  const handleSubmitOrganizationDescription = useCallback(
    (description: string) => {
      setFormData({ organizationDescription: description });
      goToStep(9);
    },
    [setFormData, goToStep],
  );

  // Complete account setup
  const handleCompleteSetup = useCallback(async () => {
    try {
      // Directly call the effect and await it (instead of dispatching event)
      await completeAccountSetupFx({
        formData: accountSetupState.formData,
        selectedOrganizations: accountSetupState.selectedOrganizations,
        expertiseTerms: expertiseTerms.data || [],
        countriesTerms: countriesTerms.data || [],
        sectorsTerms: sectorsTerms.data || [],
        fundingTypeTerms: organizationCategoriesTerms.data || [],
        submitPartnerOnly,
      });

      // Refetch user info after completion
      await fetchUserBasicInfoFx();

      // Navigate after everything completes
      if (isModal && onComplete) {
        onComplete();
      } else {
        navigate(ROUTES.SETTINGS_SUBSCRIPTION, { replace: true });
      }
    } catch (error) {
      console.error("Account setup completion failed:", error);
    }
  }, [
    accountSetupState.formData,
    accountSetupState.selectedOrganizations,
    expertiseTerms.data,
    countriesTerms.data,
    sectorsTerms.data,
    organizationCategoriesTerms.data,
    isModal,
    onComplete,
    navigate,
    submitPartnerOnly,
  ]);

  return {
    // State
    currentStep: accountSetupState.currentStep,
    formData: accountSetupState.formData,
    selectedNodes: accountSetupState.selectedNodes,
    selectedExpertise: accountSetupState.selectedExpertise,
    selectedOrgSectors: accountSetupState.selectedOrgSectors,
    orgDescription: accountSetupState.orgDescription,
    organizationSearchQuery: accountSetupState.organizationSearchQuery,
    organizationSearchResults: accountSetupState.organizationSearchResults,
    organizationSearchLoading: accountSetupState.organizationSearchLoading,
    selectedOrganizations: accountSetupState.selectedOrganizations,
    proposedPartners: accountSetupState.proposedPartners,
    proposedPartnersLoading: accountSetupState.proposedPartnersLoading,
    plans: accountSetupState.plans,
    plansLoading: accountSetupState.plansLoading,
    isCompleting: accountSetupState.isCompleting,
    completionError: accountSetupState.completionError,
    expandedSectors: accountSetupState.expandedSectors,

    // Computed
    sectorTreeNodes,
    initialExpandedKeys,
    countryOptions,
    expertiseOptions,
    skipOrgSteps,
    selectedInterests: getSelectedInterests(),
    selectedOrgSectorLabels: getSelectedOrgSectorLabels(),

    // Loading states
    sectorsLoading: sectorsTerms.loading,
    expertiseLoading: expertiseTerms.loading,
    countriesLoading: countriesTerms.loading,

    // Actions
    goToStep,
    setSelectedNodes: updateSelectedNodes,
    toggleExpertise: toggleExpertiseSelection,
    setSelectedOrgSectors: updateSelectedOrgSectors,
    setOrgDescription: updateOrgDescription,
    setSearchQuery,
    clearSearchResults,
    selectOrganization: addOrganization,
    removeOrganization: deleteOrganization,
    toggleSectorExpansion: toggleSector,
    removeInterest,
    removeOrgSector,
    convertToSearchResult,

    // Step handlers
    handleSubmitJobTitle,
    handleSubmitInterests,
    handleSubmitExpertise,
    handleSkipExpertise,
    handleCreateOrganization,
    handleJoinOrganizations,
    handleSkipOrganization,
    handleSubmitOrganizationDetails,
    handleSubmitOrganizationSectors,
    handleSkipOrganizationSectors,
    handleSubmitOrganizationDescription,
    handleCompleteSetup,
  };
};

// ============================================
// Helper Functions
// ============================================

function transformToTreeNodes(
  data: Array<{ tid: string; name: string; parent: string }>,
): TreeNode[] {
  const nodeMap: { [key: string]: TreeNode } = {};
  const rootNodes: TreeNode[] = [];

  data.forEach((item) => {
    nodeMap[item.tid] = {
      key: item.tid,
      label: item.name,
      data: item.name,
      children: [],
    };
  });

  data.forEach((item) => {
    if (item.parent === "0") {
      rootNodes.push(nodeMap[item.tid]);
    } else if (nodeMap[item.parent]) {
      nodeMap[item.parent].children!.push(nodeMap[item.tid]);
    }
  });

  rootNodes.forEach((node) => {
    if (node.children && node.children.length === 0) {
      delete node.children;
    }
  });

  return rootNodes;
}

function findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
}

function findKeyByLabel(nodes: TreeNode[], label: string): string | null {
  for (const node of nodes) {
    if (node.label === label) return node.key as string;
    if (node.children) {
      const found = findKeyByLabel(node.children, label);
      if (found) return found;
    }
  }
  return null;
}
